<template>
  <div>
    <PageHeader :title="isProfile ? 'School Profile' : (isEdit ? 'Edit School' : 'Add School')" showBack />

    <div class="card p-4">
      <b-form @submit.prevent="saveSchool">
        <h6 class="mb-3">Basic Info</h6>

        <b-form-group label="School Name" label-for="name">
          <b-form-input id="name" v-model="form.name" required />
        </b-form-group>

        <b-form-group label="Contact Email" label-for="email">
          <b-form-input id="email" v-model="form.email" type="email" />
        </b-form-group>

        <b-form-group label="Phone" label-for="phone">
          <b-form-input id="phone" v-model="form.phone" />
        </b-form-group>

        <b-form-group label="Website" label-for="website">
          <b-form-input id="website" v-model="form.website" />
        </b-form-group>

        <b-form-group label="Address" label-for="address">
          <b-form-input id="address" v-model="form.address" />
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Zipcode" label-for="zipcode">
              <b-form-input id="zipcode" v-model="form.zipcode" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="City" label-for="city">
              <b-form-input id="city" v-model="form.city" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="State" label-for="state">
              <b-form-input id="state" v-model="form.state" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Status">
              <b-form-checkbox v-model="form.is_active" switch>Active</b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <hr />
        <h6 class="mb-3">Logos &amp; Images</h6>

        <b-form-group label="Logo">
          <div class="d-flex align-items-center">
            <img
              v-if="form.logo_url"
              :src="form.logo_url"
              alt="Logo preview"
              class="mr-3"
              style="max-height: 48px; max-width: 120px; object-fit: contain;"
            />
            <b-form-file
              v-model="logoFile"
              accept="image/*"
              placeholder="Choose logo..."
              drop-placeholder="Drop file here..."
              @input="onLogoUpload"
            />
          </div>
        </b-form-group>

        <b-form-group label="Small Logo">
          <div class="d-flex align-items-center">
            <img
              v-if="form.small_logo_url"
              :src="form.small_logo_url"
              alt="Small logo preview"
              class="mr-3"
              style="max-height: 48px; max-width: 120px; object-fit: contain;"
            />
            <b-form-file
              v-model="smallLogoFile"
              accept="image/*"
              placeholder="Choose small logo..."
              drop-placeholder="Drop file here..."
              @input="onSmallLogoUpload"
            />
          </div>
        </b-form-group>

        <b-form-group label="Background Image">
          <div class="d-flex align-items-center">
            <img
              v-if="form.profile_bg_img_url"
              :src="form.profile_bg_img_url"
              alt="Background preview"
              class="mr-3"
              style="max-height: 48px; max-width: 120px; object-fit: contain;"
            />
            <b-form-file
              v-model="bgImageFile"
              accept="image/*"
              placeholder="Choose background image..."
              drop-placeholder="Drop file here..."
              @input="onBgImageUpload"
            />
          </div>
        </b-form-group>

        <hr />
        <h6 class="mb-3">Branding</h6>
        <div class="d-flex align-items-end mb-3">
          <div class="text-center mr-4">
            <label class="d-block mb-2">Primary</label>
            <label class="color-circle-picker" :style="{ backgroundColor: form.primary_color }">
              <input type="color" v-model="form.primary_color" />
            </label>
          </div>
          <div class="text-center mr-4">
            <label class="d-block mb-2">Secondary</label>
            <label class="color-circle-picker" :style="{ backgroundColor: form.secondary_color }">
              <input type="color" v-model="form.secondary_color" />
            </label>
          </div>
          <div class="text-center">
            <label class="d-block mb-2">Dark</label>
            <label class="color-circle-picker" :style="{ backgroundColor: form.dark_color }">
              <input type="color" v-model="form.dark_color" />
            </label>
          </div>
        </div>

        <hr />
        <h6 class="mb-3">External Integrations</h6>

        <b-form-group label="Transition School ID" label-for="transition_school_id">
          <b-form-input
            id="transition_school_id"
            v-model="form.transition_school_id"
            type="number"
            min="0"
            step="1"
            placeholder="Numbers only"
          />
        </b-form-group>

        <b-form-group label="TransitionEnroll School ID" label-for="enroll_school_id">
          <b-form-input
            id="enroll_school_id"
            v-model="form.enroll_school_id"
            type="number"
            min="0"
            step="1"
            placeholder="Numbers only"
          />
        </b-form-group>

        <hr />
        <h6 class="mb-3">Tenant Settings</h6>

        <b-form-group
          label="Subdomain"
          label-for="subdomain"
          description="School-branded subdomain (e.g. medical → medical.transitioncrm.com). Lowercase letters, numbers and dashes."
        >
          <b-form-input
            id="subdomain"
            v-model="form.subdomain"
            placeholder="medical"
          />
        </b-form-group>

        <b-form-group
          label="Resend From Email"
          label-for="resend_from_email"
          description="Verified Resend sender, e.g. School Name <noreply@yourdomain.com>. Falls back to the platform default if blank."
        >
          <b-form-input
            id="resend_from_email"
            v-model="form.resend_from_email"
            placeholder="School Name <noreply@yourdomain.com>"
          />
        </b-form-group>

        <b-form-group
          label="Resend Domain ID"
          label-for="resend_domain_id"
          description="Optional. The Resend domain ID used to verify this school's sending domain."
        >
          <b-form-input
            id="resend_domain_id"
            v-model="form.resend_domain_id"
            placeholder="re_..."
          />
        </b-form-group>

        <b-form-group
          label="Twilio Phone Number"
          label-for="twilio_phone_number"
          description="The Twilio phone number assigned to this school for SMS (E.164 format, e.g. +15551234567). Falls back to the platform default if blank."
        >
          <b-form-input
            id="twilio_phone_number"
            v-model="form.twilio_phone_number"
            placeholder="+15551234567"
          />
        </b-form-group>

        <!-- Owner Details (only when creating a new school) -->
        <template v-if="!isEdit">
          <hr />
          <h6 class="mb-3">Owner Details</h6>
          <b-row>
            <b-col md="6">
              <b-form-group label="First Name" label-for="owner_first_name">
                <b-form-input id="owner_first_name" v-model="owner.first_name" required />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Last Name" label-for="owner_last_name">
                <b-form-input id="owner_last_name" v-model="owner.last_name" required />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Email" label-for="owner_email">
            <b-form-input id="owner_email" v-model="owner.email" type="email" required />
          </b-form-group>

          <b-form-group label="Password">
            <b-alert show variant="info" class="mb-0 py-2 px-3">
              <small>Auto-generated: <strong>{{ generatedPassword }}</strong></small>
            </b-alert>
          </b-form-group>
        </template>

        <div class="d-flex mt-3">
          <b-button type="submit" variant="primary" :disabled="isSaving" class="mr-2">
            <b-spinner v-if="isSaving" small class="mr-1" />{{ isEdit ? 'Update' : 'Create' }}
          </b-button>
          <b-button variant="outline-secondary" @click="$router.back()">Cancel</b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

function generatePassword(length = 12) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

export default {
  name: 'PostSchool',
  components: { PageHeader },
  data() {
    return {
      isSaving: false,
      form: this.getEmptyForm(),
      owner: {
        first_name: '',
        last_name: '',
        email: '',
      },
      generatedPassword: generatePassword(),
      logoFile: null,
      smallLogoFile: null,
      bgImageFile: null,
    };
  },
  computed: {
    isProfile() {
      return this.$route.name === 'school-profile';
    },
    isEdit() {
      return !!this.$route.params.id || this.isProfile;
    },
    schoolId() {
      if (this.$route.params.id) return this.$route.params.id;
      if (this.isProfile) {
        const user = this.$store.getters['auth/getLoggedInUser'];
        return user && user.school ? user.school.id : null;
      }
      return null;
    },
  },
  methods: {
    ...mapActions('school', ['getSchool', 'createSchool', 'updateSchool']),
    ...mapActions('user', ['createUser']),
    getEmptyForm() {
      return {
        name: '', email: '', phone: '', website: '',
        address: '', city: '', state: '', zipcode: '',
        logo_url: '', small_logo_url: '', profile_bg_img_url: '',
        primary_color: '#3B86F5', secondary_color: '#2F6FED', dark_color: '#181818',
        transition_school_id: null, enroll_school_id: null,
        subdomain: '', resend_from_email: '', resend_domain_id: '', twilio_phone_number: '',
        is_active: true,
      };
    },
    onLogoUpload(file) {
      if (file) {
        this.form.logo_url = URL.createObjectURL(file);
      }
    },
    onSmallLogoUpload(file) {
      if (file) {
        this.form.small_logo_url = URL.createObjectURL(file);
      }
    },
    onBgImageUpload(file) {
      if (file) {
        this.form.profile_bg_img_url = URL.createObjectURL(file);
      }
    },
    buildFormData() {
      const fd = new FormData();
      Object.keys(this.form).forEach(key => {
        if (key === 'logo_url' || key === 'small_logo_url' || key === 'profile_bg_img_url') return;
        if (this.form[key] !== null && this.form[key] !== undefined) {
          fd.append(key, this.form[key]);
        }
      });
      if (this.logoFile) fd.append('logo', this.logoFile);
      if (this.smallLogoFile) fd.append('small_logo', this.smallLogoFile);
      if (this.bgImageFile) fd.append('profile_bg_img', this.bgImageFile);
      return fd;
    },
    async loadSchool() {
      if (!this.isEdit || !this.schoolId) return;
      try {
        const res = await this.getSchool(this.schoolId);
        const s = res.data || res.results || res;
        Object.keys(this.form).forEach(key => {
          if (s[key] !== undefined) this.form[key] = s[key];
        });
      } catch (err) {
        this.$bvToast && this.$bvToast.toast('Failed to load school', { variant: 'danger', solid: true });
      }
    },
    async saveSchool() {
      this.isSaving = true;
      try {
        const hasFiles = this.logoFile || this.smallLogoFile || this.bgImageFile;

        if (this.isEdit) {
          const data = hasFiles ? this.buildFormData() : this.form;
          await this.updateSchool({ id: this.schoolId, data });
          this.$bvToast && this.$bvToast.toast('School updated', { variant: 'success', solid: true });
        } else {
          // Step 1: Create the school
          const schoolPayload = hasFiles ? this.buildFormData() : { ...this.form };
          const schoolRes = await this.createSchool(schoolPayload);
          const schoolId = schoolRes.data?.id || schoolRes.id;

          // Step 2: Create the owner user linked to the school
          if (this.owner.email) {
            try {
              await this.createUser({
                first_name: this.owner.first_name,
                last_name: this.owner.last_name,
                email: this.owner.email,
                password: this.generatedPassword,
                role: 'school_owner',
                school: schoolId,
                is_active: true,
              });
            } catch (userErr) {
              const userMsg = userErr.response?.data?.detail || userErr.response?.data?.message || 'School created but failed to create owner user';
              this.$bvToast && this.$bvToast.toast(userMsg, { variant: 'warning', solid: true });
            }
          }

          this.$bvToast && this.$bvToast.toast('School created', { variant: 'success', solid: true });
          this.$router.push({ name: 'manage-schools' });
        }
      } catch (err) {
        const msg = err.response?.data?.detail || err.response?.data?.message || 'Save failed';
        this.$bvToast && this.$bvToast.toast(msg, { variant: 'danger', solid: true });
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.loadSchool();
  },
};
</script>

<style scoped>
.color-circle-picker {
  display: inline-block;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid #e3e6f0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.15s ease;
}
.color-circle-picker:hover {
  border-color: #b7bac1;
}
.color-circle-picker input[type="color"] {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 70px;
  height: 70px;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0;
}
</style>
